import React, {ReactNode, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {useScrollToHash} from "../projectBits";

export interface DocSection {
    id: string;
    title: string;
    body: ReactNode;
}

interface DocPageProps {
    title: string;
    updated: string;
    intro: ReactNode;
    sections: DocSection[];
    crossLink: {to: string; label: string};
}

// Long-form text page (earthPlace privacy policy and support): sections with
// an "On this page" index on desktop that tracks the section being read.
export function DocPage({title, updated, intro, sections, crossLink}: DocPageProps) {
    const [current, setCurrent] = useState(sections[0]?.id);
    const rootRef = useRef<HTMLDivElement>(null);
    // after clicking an entry, keep it highlighted until that scroll finishes
    const pinnedRef = useRef<{id: string; timer?: number} | null>(null);
    useScrollToHash(true);

    useEffect(() => {
        const scroller = rootRef.current?.closest<HTMLElement>(".vignette");
        if (!scroller) return;
        const onScroll = () => {
            const pinned = pinnedRef.current;
            if (pinned) {
                window.clearTimeout(pinned.timer);
                pinned.timer = window.setTimeout(() => { pinnedRef.current = null; }, 150);
                return;
            }
            // The line a section must pass to become current slides down the
            // screen as you scroll: at the top of the page it sits at the top
            // (first section), at the bottom it sits at the bottom (last
            // section), so short sections at the end can still be reached.
            const range = scroller.scrollHeight - scroller.clientHeight;
            const progress = range > 0 ? scroller.scrollTop / range : 0;
            const line = scroller.getBoundingClientRect().top + scroller.clientHeight * progress + 1;
            let id = sections[0]?.id;
            for (const s of sections) {
                const el = document.getElementById(s.id);
                if (el && el.getBoundingClientRect().top <= line) id = s.id;
            }
            setCurrent(id);
        };
        onScroll();
        scroller.addEventListener("scroll", onScroll, {passive: true});
        return () => scroller.removeEventListener("scroll", onScroll);
    }, [sections]);

    const jumpTo = (id: string) => {
        setCurrent(id);
        pinnedRef.current = {id, timer: window.setTimeout(() => { pinnedRef.current = null; }, 1000)};
        document.getElementById(id)?.scrollIntoView({behavior: "smooth", block: "start"});
    };

    return (
        <div className="doc" ref={rootRef}>
            <div className="doc-head">
                <h2>{title}</h2>
                <p className="pj-muted">Last updated {updated}</p>
            </div>
            <div className="doc-layout">
                <div className="doc-sections">
                    {intro && <div className="doc-intro">{intro}</div>}
                    {sections.map(s => (
                        <section key={s.id} id={s.id} className="doc-section">
                            <h3>{s.title}</h3>
                            <hr/>
                            {s.body}
                        </section>
                    ))}
                </div>
                <nav className="doc-toc">
                    {sections.map(s => (
                        <button key={s.id} className={s.id === current ? "on" : ""} onClick={() => jumpTo(s.id)}>
                            {s.title}
                        </button>
                    ))}
                    <hr/>
                    <Link to={crossLink.to}>{crossLink.label}</Link>
                </nav>
            </div>
        </div>
    );
}
